// /app/:clientId/finance (finance redesign slice 2, slice 13; finance-real, the user 2026-10-22): one client's money on
// one page, as three tables the header's TabGroup picks, in the order the history runs: Quotes, Jobs and Invoices, Jobs
// by default (`view`). A job is one line item for one period (Social Media Management · October 2026, a one-off), with
// its own rhythm, price, margin % and the claims the team makes on it; each next month is a job of its own, made on the
// month's first day while the series repeats. A quote is a bundle of jobs, and its jobs become the client's agreed ones
// once it is accepted, each then started on its own; a job can also start on request, with no quote. An invoice is a
// collection of done or active jobs, any period mixed. A quote and an invoice never name each other: jobs are in between.
// The Jobs table shows one status (`show`): Draft, Quoting, Agreed, Active, Paused, Done, Invoiced or Dropped, each with
// its count; Quotes its Draft, Proposed, Accepted or Declined ones (Proposed by default) and Invoices its Draft, Sent,
// Overdue, Partly paid or Paid ones (Sent by
// default; Overdue is its own tab, as the status the server gives a sent invoice 30 days on). A default with no rows gives
// way to the first status that has any (showTab): a
// list's tabs are its statuses (DD-062). Each footer holds the server's totals for its tab (TD-102). Every status cell is
// the platform's status dropdown, as an idea row's is (IdeaRow, DD-062): Active for a job not started opens its drawer to
// ask the day it starts (`confirm=start`), Paid for an invoice its drawer's payment step (`confirm=paid`); a quote's every
// status is one write, Accepted too. A job row opens its drawer (LineJobDrawer), a quote
// row its drawer (QuoteDrawer), an invoice row its drawer (JobInvoiceDrawer). A row's other steps are its ⋮ (DD-045). A
// modal is named in the URL and replaces any other (DD-045), but for a new job made from a quote or a draft invoice, whose
// form stacks over it (`stack`). Each view's create button sits on the header's right (DD-056). The agency's own client takes no quote and
// sends no invoice (TD-105); which client it is, is set in Setup (the user 2026-10-22), not here. Main's contract-month
// drawers are retired from every click here (the user 2026-10-23): the month, its invoice, Send, Mark paid, Edit split,
// Split, Who does what and Add a cost; a job, a quote and an invoice each have their own drawer.
import { useRef } from "react";
import { useParams } from "react-router-dom";
import { SpinnerIcon } from "@/components/icons";
import { PageHeader, PageLayout } from "@/components/PageLayout";
import { TablePageBody } from "@/components/PageLayout/TablePageBody";
import { BadgeDot } from "@/components/ui/Badge";
import { DataTable, type DataColumn } from "@/components/ui/DataTable";
import type { DropdownMenuEntry } from "@/components/ui/DropdownMenu";
import { MoneyText } from "@/components/ui/MoneyText";
import { TabGroup } from "@/components/ui/TabGroup";
import { ENTITY_CONFIG, EntityCreateButton } from "@/core/entityConfig";
import { ModalPending } from "../components/ModalPending";
import { RetryBlock } from "../components/RetryBlock";
import { TotalsFooter, type TotalItem } from "../components/TotalsFooter";
import { ExpenseEntryModal } from "../expenses/ExpenseEntryModal";
import { ExpenseItemModal } from "../expenses/ExpenseItemModal";
import { useFinanceParams, type ParamPatch } from "../hooks/useFinanceParams";
import { useRequestKeys } from "../hooks/useRequestKeys";
import type {
  ContractTotals, JobInvoice, JobInvoiceStatus, JobInvoiceTotals, LineJob, LineJobPickedStatus, LineJobStatus, LineJobTotals, QuoteBundle, QuoteBundleStatus,
} from "../types";
import { invoiceDueNote } from "../utils/flows";
import { awaitsPayment, invoiceDateLine, jobsCount, jobSubline, withCount } from "../utils/names";
import { showTab } from "../utils/showTabs";
import { useOpenerFocus } from "../agency/useOpenerFocus";
import { useClientFinancePage } from "./hooks/useClientFinancePage";
import { InvoiceStatusBadge, JobInvoiceDrawer, NewJobInvoiceDrawer, toBill } from "./JobInvoiceDrawers";
import { JobStatusBadge } from "./JobStatusBadge";
import { LineJobDrawer, needsStart, onOpenQuote } from "./LineJobDrawer";
import { LineJobFormDrawer } from "./LineJobForms";
import { JOB_INVOICE_STATES, JOB_INVOICE_STATUSES, JOB_STATUS, JOB_STATUSES, jobInvoiceTitle, QUOTE_STATUS, QUOTE_STATUSES } from "./lineJobText";
import { QuoteDrawer, QuoteFormDrawer, QuoteStatusBadge } from "./QuoteDrawers";
import { ReviewMark } from "./ReviewMark";

const clientWord = ENTITY_CONFIG.client.label.toLowerCase();
const quoteWord = ENTITY_CONFIG.quote.label.toLowerCase();
const invoiceWord = ENTITY_CONFIG.invoice.label.toLowerCase();
// The user's job, a line item, rides main's `contract` key (the entityConfig shadow); `job` is main's month.
const JOB = ENTITY_CONFIG.contract;
const jobWord = JOB.label.toLowerCase();

type View = "quotes" | "jobs" | "invoices";
const VIEWS: View[] = ["quotes", "jobs", "invoices"];
const TAB_CONFIG = { variant: "navigation", size: "sm", layout: "horizontal", hasBackground: true } as const;
// A tab keeps its whole label (shrink-0): on a phone the track scrolls sideways instead of truncating them, as /finance's.
const TRACK = "min-w-0 max-w-full overflow-x-auto [scrollbar-width:none]";
// Opening a modal clears every other modal param in the same call: one set per handler.
const NO_MODAL: ParamPatch = {
  modal: null, mode: null, contract: null, quote: null, change: null, confirm: null, job: null, work: null, line: null, entry: null, tpl: null, start: null, invoice: null, lineJob: null, stack: null,
};

/** A status tab's jobs added up, the server's (TD-102): each job is one period, so they add. The own client's are what it
 * pays its team (TD-105). Every tab leads with Price, the column's name; Dropped stops there, as nothing on it is done,
 * claimed or kept. */
export function jobTotalsOf(t: LineJobTotals, status: LineJobStatus, own: boolean): TotalItem[] {
  if (t.count === 0) return [];
  if (own) return [{ label: "Costs", amountMinor: t.claimedMinor }];
  if (status === "dropped") return [{ label: "Price", amountMinor: t.priceMinor }];
  return [
    { label: "Price", amountMinor: t.priceMinor },
    { label: "Claimed", amountMinor: t.claimedMinor },
    { label: "Margin", amountMinor: t.marginMinor },
  ];
}

/** A tab's quotes added up, under the columns' and the drawer's words (R7), the qualifier last as in "Margin each month". */
function quoteTotalsOf(t: ContractTotals): TotalItem[] {
  return [
    ...(t.monthlyMinor !== 0 ? [{ label: "Each month", amountMinor: t.monthlyMinor }, { label: "Margin each month", amountMinor: t.monthlyMarginMinor }] : []),
    ...(t.oneOffMinor !== 0 ? [{ label: "One-off", amountMinor: t.oneOffMinor }, { label: "Margin one-off", amountMinor: t.oneOffMarginMinor }] : []),
  ];
}

/** A status tab's invoices added up, the server's (TD-102). */
export function invoiceTotalsOf(t: JobInvoiceTotals, status: JobInvoiceStatus): TotalItem[] {
  if (t.count === 0) return [];
  switch (status) {
    case "draft":
      return [{ label: "To send", amountMinor: t.invoiceMinor }];
    case "sent":
    case "overdue":
      return [{ label: "Still due", amountMinor: t.dueMinor }];
    case "partial":
      return [
        // "Paid so far", as its rows say "paid": Received is a team invoice's status.
        { label: "Paid so far", amountMinor: t.receivedMinor },
        { label: "Still due", amountMinor: t.dueMinor },
      ];
    case "paid":
      return [{ label: "Paid", amountMinor: t.invoiceMinor }];
  }
}

const QUOTE_EMPTY: Record<QuoteBundleStatus, string> = {
  draft: `No draft ${quoteWord}.`,
  proposed: `No ${quoteWord} out with the ${clientWord}.`,
  accepted: `No ${quoteWord} accepted yet.`,
  declined: `No ${quoteWord} declined.`,
};

const INVOICE_EMPTY: Record<JobInvoiceStatus, string> = {
  draft: `No draft ${invoiceWord}. ${ENTITY_CONFIG.invoice.createLabel} makes one from ${JOB.plural.toLowerCase()} done or under way.`,
  sent: `No ${invoiceWord} waiting to be paid.`,
  overdue: `No ${invoiceWord} overdue: none sent more than 30 days ago still waits for money.`,
  partial: `No ${invoiceWord} partly paid.`,
  paid: `No ${invoiceWord} paid yet.`,
};

const EMPTY: Record<LineJobStatus, string> = {
  draft: `No draft ${jobWord}. One made here as a draft, or on a draft ${quoteWord}, waits here.`,
  quoting: `No ${jobWord} on a proposed ${quoteWord}.`,
  agreed: `No ${jobWord} agreed. An accepted ${quoteWord}'s ${JOB.plural.toLowerCase()} wait here, each until its status starts it.`,
  active: `No active ${jobWord}.`,
  paused: `No ${jobWord} paused. A paused one makes no next ${jobWord}, and no ${invoiceWord} takes it, until it is active again.`,
  done: `No ${jobWord} done. A ${jobWord} is done once marked, or once its period ends, and waits here for an ${invoiceWord}.`,
  invoiced: `No ${jobWord} on an ${invoiceWord} yet.`,
  dropped: `No ${jobWord} dropped.`,
};

export function ClientFinancePage() {
  // Data follows the URL, not the persisted store selection, so a client switch never shows stale data.
  const { clientId } = useParams<{ clientId: string }>();
  const { params, set, closeModals } = useFinanceParams();
  const { jobsQuery, claimsQuery, invoicesQuery, peopleQuery, updateQuote, setStatus, setQuoteStatus, setInvoiceStatus } = useClientFinancePage(clientId, {
    // R6: the invoices read loads on the Invoices view or under an invoice drawer only.
    invoices: params.get("view") === "invoices" || params.get("modal") === "job-invoice",
  });
  const modal = params.get("modal");
  // Every drawer opened from the page, the row or button that opened it is focused again once none is open (the S6
  // walk: closing one left focus on the body). A hand-over from one drawer to the next keeps the first opener. With no
  // opener left on the page (its row moved tab, the view changed, a link or a reload opened the drawer) focus goes to the
  // table's first row, else the create button, else the view's tab (the S8 walk).
  const pageRef = useRef<HTMLDivElement>(null);
  const markOpener = useOpenerFocus(modal, () => [
    pageRef.current?.querySelector<HTMLElement>('tbody tr[tabindex="0"]'),
    pageRef.current?.querySelector<HTMLElement>("[data-opener-fallback] button"),
    pageRef.current?.querySelector<HTMLElement>('button[aria-current="true"]'),
  ]);
  // A row's Draft or Sent sends a request key, kept on a retry of the same pick (TD-077).
  const keys = useRequestKeys();
  const go = (patch: ParamPatch, replace?: boolean) => {
    if (modal === null) markOpener();
    set({ ...NO_MODAL, ...patch }, replace);
  };

  if (!clientId) {
    return <div className="flex-1 flex items-center justify-center text-sm text-gray-500">{`No ${clientWord} selected`}</div>;
  }
  const data = jobsQuery.data;
  // Nothing that reads either renders before the jobs answer.
  const own = data?.ownClient ?? false;
  const today = data?.today ?? "";
  const jobs = data?.jobs ?? [];
  const quotes = data?.quotes ?? [];
  const people = peopleQuery.data ?? [];
  const invoiceRead = invoicesQuery.data;
  const invoices = invoiceRead?.invoices ?? [];
  const claimants = (job: LineJob): string[] => (claimsQuery.data ?? []).filter((c) => c.jobId === job.id).map((c) => c.name);
  const stacked = params.get("stack") === "line-job-form";
  const lineJob = jobs.find((j) => j.id === params.get("lineJob"));
  const quote = quotes.find((q) => q.id === params.get("quote"));
  const invoice = invoices.find((i) => i.id === params.get("invoice"));
  const close = () => closeModals(NO_MODAL);
  const view: View = VIEWS.find((v) => v === params.get("view")) ?? "jobs";
  // With no `show=`, each list opens on its default while it has rows, else the first status with any (showTab).
  const status: LineJobStatus = showTab(params.get("show"), JOB_STATUSES, "active", data ? (s) => data.jobTotals[s].count : null);
  const quoteShow: QuoteBundleStatus = showTab(params.get("show"), QUOTE_STATUSES, "proposed", data ? (s) => quotes.filter((q) => q.status === s).length : null);
  const invoiceShow: JobInvoiceStatus = showTab(params.get("show"), JOB_INVOICE_STATUSES, "sent", invoiceRead ? (s) => invoiceRead.invoiceTotals[s].count : null);
  const quoteOf = (job: LineJob): QuoteBundle | undefined => quotes.find((q) => q.id === job.quoteId);
  const jobsOf = (ids: string[]): LineJob[] => ids.flatMap((id) => jobs.filter((j) => j.id === id));
  // What a quote picks from: the client's drafts on no quote (the user: "select from the jobs added under the jobs tab").
  const drafts = jobs.filter((j) => j.status === "draft" && j.quoteId === null);
  const billable = jobs.filter(toBill);

  // A job's steps: from its row's ⋮ they push, from its drawer's they replace the drawer (DD-045, R1). Its fields are
  // edited in its drawer, and its status is the dropdown.
  const actionsOf = (job: LineJob, inDrawer = false): DropdownMenuEntry[] => {
    const q = quoteOf(job);
    const items: DropdownMenuEntry[] = [];
    if (q) items.push({ id: "quote", label: `Open its ${quoteWord}`, onClick: () => go({ modal: "quote", quote: q.id }, inDrawer) });
    if (q && (q.status === "draft" || q.status === "proposed")) {
      items.push({ id: "off-quote", label: `Take off the ${quoteWord}`, onClick: () => updateQuote.mutate({ quoteId: q.id, input: { jobIds: q.jobIds.filter((id) => id !== job.id) } }) });
    }
    if (job.invoice) {
      const invoiceId = job.invoice.id;
      items.push({ id: "invoice", label: `Open its ${invoiceWord}`, onClick: () => go({ modal: "job-invoice", invoice: invoiceId, view: "invoices" }, inDrawer) });
    }
    return items;
  };

  // A row's status pick: Active for a job not started asks its day in the drawer.
  const pickStatus = (job: LineJob, picked: LineJobPickedStatus) => {
    if (picked === "active" && needsStart(job)) return go({ modal: "line-job", lineJob: job.id, confirm: "start" });
    setStatus.mutate({ jobId: job.id, input: { status: picked, startsOn: null } });
  };
  const jobColumns: DataColumn<LineJob>[] = [
    {
      key: "job", label: JOB.label, truncates: true,
      render: (j) => (
        <span className="flex items-center gap-2 min-w-0">
          {/* DD-055: below sm the Status column hides, and the status rides the name as a dot. */}
          <BadgeDot tone={JOB_STATUS[j.status].tone} label={JOB_STATUS[j.status].label} className="sm:hidden" />
          <span className="flex flex-col min-w-0">
            <span className="font-medium text-gray-900 truncate">{j.name}</span>
            <span className="text-xs text-gray-500 truncate">{jobSubline(j)}</span>
          </span>
        </span>
      ),
    },
    {
      key: "state", label: "Status", width: "hidden sm:table-cell w-32",
      render: (j) => (
        // The row opens the drawer; the dropdown is the cell's own, as on an idea row.
        <span onClick={(e) => e.stopPropagation()}>
          <JobStatusBadge job={j} onOpenQuote={onOpenQuote(j, quoteOf(j))} onPick={(s) => pickStatus(j, s)} loading={setStatus.isPending && setStatus.variables?.jobId === j.id} />
        </span>
      ),
    },
    {
      key: "who", label: "Who", width: "hidden lg:table-cell w-44",
      render: (j) => (
        <span className="block truncate text-gray-600">
          {claimants(j).join(", ") || "·"}
          {j.overCap && <span className="text-xs text-amber-700">{" · over the cap"}</span>}
        </span>
      ),
    },
    {
      key: "price", label: own ? "Costs" : "Price", align: "right", width: "w-32",
      render: (j) => (
        <ReviewMark review={j.review}>
          <MoneyText amountMinor={own ? j.claimedMinor : j.priceMinor} />
        </ReviewMark>
      ),
    },
    ...(own
      ? []
      : [
          {
            key: "margin", label: "Margin", align: "right" as const, width: "hidden sm:table-cell w-32",
            render: (j: LineJob) => (
              <ReviewMark review={j.review}>
                <MoneyText amountMinor={j.marginMinor} balance />
              </ReviewMark>
            ),
          },
        ]),
  ];
  // A quote row's status pick: one write, Accepted too, which asks no day.
  const pickQuoteStatus = (q: QuoteBundle, picked: QuoteBundleStatus) => setQuoteStatus.mutate({ quoteId: q.id, input: { status: picked, startsOn: null } });
  // An invoice row's status pick: Paid records a payment in the drawer; Partly paid is a payment's.
  const pickInvoiceStatus = (i: JobInvoice, picked: JobInvoiceStatus) => {
    if (picked === "paid") return go({ modal: "job-invoice", invoice: i.id, confirm: "paid" });
    if (picked !== "draft" && picked !== "sent") return;
    const scope = `${i.id}:${picked}`;
    setInvoiceStatus.mutate({ invoiceId: i.id, input: { status: picked, requestId: keys.keyFor(scope) } }, { onSuccess: () => keys.settled(scope), onError: (error) => keys.settled(scope, error) });
  };
  const quoteColumns: DataColumn<QuoteBundle>[] = [
    {
      key: "quote", label: ENTITY_CONFIG.quote.label, truncates: true,
      render: (q) => (
        <span className="flex items-center gap-2 min-w-0">
          <BadgeDot tone={QUOTE_STATUS[q.status].tone} label={QUOTE_STATUS[q.status].label} className="sm:hidden" />
          <span className="flex flex-col min-w-0">
            <span className="font-medium text-gray-900 truncate">{q.title}</span>
            <span className="text-xs text-gray-500 truncate">{jobsOf(q.jobIds).map((j) => j.name).join(" · ") || `No ${jobWord} yet`}</span>
          </span>
        </span>
      ),
    },
    {
      key: "state", label: "Status", width: "hidden sm:table-cell w-36",
      render: (q) => <QuoteStatusBadge quote={q} onPick={(s) => pickQuoteStatus(q, s)} loading={setQuoteStatus.isPending && setQuoteStatus.variables?.quoteId === q.id} />,
    },
    { key: "monthly", label: "Each month", align: "right", width: "w-32", render: (q) => (q.monthlyMinor !== 0 ? <MoneyText amountMinor={q.monthlyMinor} /> : <span className="text-gray-400">·</span>) },
    { key: "oneOff", label: "One-off", align: "right", width: "hidden sm:table-cell w-32", render: (q) => (q.oneOffMinor !== 0 ? <MoneyText amountMinor={q.oneOffMinor} /> : <span className="text-gray-400">·</span>) },
  ];
  const invoiceNote = (i: JobInvoice): string | null =>
    awaitsPayment(i.status) && i.dueMinor !== i.invoiceMinor ? invoiceDueNote(i.receivedMinor, i.dueMinor, "EUR") : null;
  const invoiceColumns: DataColumn<JobInvoice>[] = [
    {
      key: "invoice", label: ENTITY_CONFIG.invoice.label, truncates: true,
      render: (i) => (
        <span className="flex items-center gap-2 min-w-0">
          <BadgeDot tone={JOB_INVOICE_STATES[i.status].tone} label={JOB_INVOICE_STATES[i.status].label} className="sm:hidden" />
          <span className="flex flex-col min-w-0">
            <span className="font-medium text-gray-900 truncate">{jobInvoiceTitle(i)}</span>
            {/* Its date line and count, as every invoice reads (invoiceDateLine); its jobs are its drawer's. */}
            <span className="text-xs text-gray-500 truncate">{withCount(invoiceDateLine({ kind: "client", ...i }), jobsCount(i.jobIds.length))}</span>
          </span>
        </span>
      ),
    },
    {
      key: "state", label: "Status", width: "hidden sm:table-cell w-36",
      render: (i) => <InvoiceStatusBadge invoice={i} onPick={(s) => pickInvoiceStatus(i, s)} loading={setInvoiceStatus.isPending && setInvoiceStatus.variables?.invoiceId === i.id} />,
    },
    {
      // DD-054: the invoice's total is Total, its drawer's word too; the name column is Invoice.
      key: "amount", label: "Total", align: "right", width: "w-32",
      render: (i) => {
        const note = invoiceNote(i);
        return (
          <span className="flex flex-col items-end">
            <MoneyText amountMinor={i.invoiceMinor} />
            {note && <span className="text-xs text-gray-500">{note}</span>}
          </span>
        );
      },
    },
  ];

  // The Library's pattern: what the page lists on the left, each with its own create button on the right (DD-012, DD-056).
  // The quote comes first: it starts the client's history.
  const views = (
    <TabGroup
      items={[
        { id: "quotes", label: ENTITY_CONFIG.quote.plural, className: "shrink-0" },
        { id: "jobs", label: JOB.plural, className: "shrink-0" },
        { id: "invoices", label: ENTITY_CONFIG.invoice.plural, className: "shrink-0" },
      ]}
      activeId={view}
      onTabChange={(id) => set({ view: id === "jobs" ? null : id, show: null }, true)}
      config={TAB_CONFIG}
      className={TRACK}
    />
  );
  const statuses =
    view === "jobs" ? (
      <TabGroup
        items={JOB_STATUSES.map((s) => ({ id: s, label: JOB_STATUS[s].label, badge: data ? data.jobTotals[s].count : undefined, className: "shrink-0" }))}
        activeId={status}
        onTabChange={(id) => set({ show: id }, true)}
        config={TAB_CONFIG}
        className={TRACK}
      />
    ) : view === "quotes" ? (
      <TabGroup
        items={QUOTE_STATUSES.map((s) => ({ id: s, label: QUOTE_STATUS[s].label, badge: data ? quotes.filter((q) => q.status === s).length : undefined, className: "shrink-0" }))}
        activeId={quoteShow}
        onTabChange={(id) => set({ show: id }, true)}
        config={TAB_CONFIG}
        className={TRACK}
      />
    ) : (
      <TabGroup
        items={JOB_INVOICE_STATUSES.map((s) => ({ id: s, label: JOB_INVOICE_STATES[s].label, badge: invoiceRead ? invoiceRead.invoiceTotals[s].count : undefined, className: "shrink-0" }))}
        activeId={invoiceShow}
        onTabChange={(id) => set({ show: id }, true)}
        config={TAB_CONFIG}
        className={TRACK}
      />
    );
  const create = !data ? null : view === "quotes" ? (
    !own && <EntityCreateButton entity="quote" variant="primary" size="sm" onClick={() => go({ modal: "quote-form" })} />
  ) : view === "invoices" ? (
    // An invoice collects done or active jobs, any period mixed.
    !own && billable.length > 0 && <EntityCreateButton entity="invoice" variant="primary" size="sm" onClick={() => go({ modal: "job-invoice", invoice: "new" })} />
  ) : (
    <EntityCreateButton entity="contract" variant="primary" size="sm" onClick={() => go({ modal: "line-job-form" })} />
  );
  const emptyOf = (): string => {
    const name = data?.client.name ?? `This ${clientWord}`;
    if (view === "quotes") return own ? `The agency's own ${clientWord} takes no ${quoteWord}.` : QUOTE_EMPTY[quoteShow];
    if (view === "invoices") return own ? `The agency's own ${clientWord} sends no ${invoiceWord}.` : INVOICE_EMPTY[invoiceShow];
    // DD-009: a client with no job yet names the buttons that start one.
    if (jobs.length === 0) return `${name} has no ${jobWord} yet. Start one with ${JOB.createLabel}, or with a ${quoteWord}.`;
    return EMPTY[status];
  };

  // R7: the Invoices table waits for its own read, and says so when it fails.
  const table = !data ? null : view === "invoices" ? (
    !invoiceRead ? (
      <div className="flex-1 min-h-0 flex items-center justify-center px-4">
        {invoicesQuery.isError ? (
          <RetryBlock
            message={`Could not load this ${clientWord}'s ${ENTITY_CONFIG.invoice.plural.toLowerCase()}.`}
            retrying={invoicesQuery.isFetching}
            onRetry={() => void invoicesQuery.refetch()}
          />
        ) : (
          <SpinnerIcon className="w-6 h-6 text-gray-400" />
        )}
      </div>
    ) : (
      <DataTable
        columns={invoiceColumns}
        rows={invoices.filter((i) => i.status === invoiceShow)}
        rowKey={(i) => i.id}
        onRowClick={(i) => go({ modal: "job-invoice", invoice: i.id })}
        empty={emptyOf()}
        footer={invoiceTotalsOf(invoiceRead.invoiceTotals[invoiceShow], invoiceShow).length > 0 ? <TotalsFooter items={invoiceTotalsOf(invoiceRead.invoiceTotals[invoiceShow], invoiceShow)} /> : undefined}
      />
    )
  ) : view === "quotes" ? (
    <DataTable
      columns={quoteColumns}
      rows={quotes.filter((q) => q.status === quoteShow)}
      rowKey={(q) => q.id}
      onRowClick={(q) => go({ modal: "quote", quote: q.id })}
      empty={emptyOf()}
      footer={quoteTotalsOf(data.quoteTotals[quoteShow]).length > 0 ? <TotalsFooter items={quoteTotalsOf(data.quoteTotals[quoteShow])} /> : undefined}
    />
  ) : (
    <DataTable
      columns={jobColumns}
      rows={jobs.filter((j) => j.status === status)}
      rowKey={(j) => j.id}
      onRowClick={(j) => go({ modal: "line-job", lineJob: j.id })}
      // DD-045: a row with no other action has no menu, so the column shows only when a row here has one.
      rowMenu={jobs.some((j) => j.status === status && actionsOf(j).length > 0) ? (j) => actionsOf(j) : undefined}
      empty={emptyOf()}
      footer={jobTotalsOf(data.jobTotals[status], status, own).length > 0 ? <TotalsFooter items={jobTotalsOf(data.jobTotals[status], status, own)} /> : undefined}
    />
  );

  const body = !data ? (
    <div className="flex-1 min-h-0 flex items-center justify-center px-4">
      {jobsQuery.isError ? (
        <RetryBlock message={`Could not load this ${clientWord}'s finance.`} retrying={jobsQuery.isFetching} onRetry={() => void jobsQuery.refetch()} />
      ) : (
        <SpinnerIcon className="w-6 h-6 text-gray-400" />
      )}
    </div>
  ) : (
    <TablePageBody>{table}</TablePageBody>
  );
  // A job's form hands back to its drawer, a quote's form to the quote's, New invoice to the draft it made. A quote's job
  // picked Active, not started, opens asking its day.
  const backToJob = (id: string, starting = false) => go({ modal: "line-job", lineJob: id, confirm: starting ? "start" : null }, true);
  const backToQuote = (id: string) => go({ modal: "quote", quote: id }, true);
  const stack = (on: boolean) => set({ stack: on ? "line-job-form" : null }, true);
  // A quote's form ticks from its own jobs and the client's drafts on no quote.
  const quoteChoices = quote ? [...jobsOf(quote.jobIds), ...drafts] : drafts;

  return (
    <PageLayout>
      {/* `contents` leaves the layout as it was: the ref only scopes where focus falls back to. */}
      <div ref={pageRef} className="contents">
        <PageHeader>
          <PageHeader.Row className="flex-wrap gap-y-2">
            {views}
            <PageHeader.Actions>
              {statuses}
              <span data-opener-fallback className="contents">
                {create}
              </span>
            </PageHeader.Actions>
          </PageHeader.Row>
        </PageHeader>
        {body}
      </div>
      {data && modal === "line-job" && lineJob && (
        <LineJobDrawer
          key={lineJob.id}
          job={lineJob}
          quote={quoteOf(lineJob)}
          people={people}
          own={own}
          today={today}
          actions={actionsOf(lineJob, true)}
          starting={params.get("confirm") === "start"}
          onOpenQuote={backToQuote}
          onOpenInvoice={(id) => go({ modal: "job-invoice", invoice: id }, true)}
          onClose={close}
        />
      )}
      {data && modal === "line-job-form" && (
        <LineJobFormDrawer
          clientId={clientId}
          own={own}
          today={today}
          // A job made on the Jobs tab opens its drawer, where its claims are recorded.
          onSaved={(job) => backToJob(job.id)}
          onClose={close}
        />
      )}
      {data && !own && modal === "quote" && quote && (
        <QuoteDrawer
          key={quote.id}
          quote={quote}
          jobs={jobsOf(quote.jobIds)}
          drafts={drafts}
          clientId={clientId}
          today={today}
          stacked={stacked}
          onStack={() => stack(true)}
          onUnstack={() => stack(false)}
          onOpenJob={backToJob}
          onEdit={() => go({ modal: "quote-form", quote: quote.id }, true)}
          onClose={close}
        />
      )}
      {data && !own && modal === "quote-form" && (params.get("quote") === null || quote) && (
        <QuoteFormDrawer
          key={params.get("quote") ?? "new"}
          clientId={clientId}
          quote={quote}
          choices={quoteChoices}
          today={today}
          stacked={stacked}
          onStack={() => stack(true)}
          onUnstack={() => stack(false)}
          onSaved={backToQuote}
          onClose={quote ? () => backToQuote(quote.id) : close}
        />
      )}
      {data && !own && modal === "job-invoice" && params.get("invoice") === "new" && (
        <NewJobInvoiceDrawer clientId={clientId} billable={billable} onCreated={(id) => go({ modal: "job-invoice", invoice: id, view: "invoices", show: "draft" }, true)} onClose={close} />
      )}
      {/* Opened from a job's drawer before the invoices are in: the drawer waits, or says why it cannot load (R7). */}
      {data && modal === "job-invoice" && params.get("invoice") !== "new" && params.get("invoice") !== null && !invoiceRead && (
        <ModalPending
          title={ENTITY_CONFIG.invoice.label}
          onClose={close}
          failure={invoicesQuery.isError ? `Could not load this ${clientWord}'s ${ENTITY_CONFIG.invoice.plural.toLowerCase()}.` : null}
          retrying={invoicesQuery.isFetching}
          retry={() => void invoicesQuery.refetch()}
        />
      )}
      {/* A link to an invoice since gone (a draft whose last job left): the drawer says so, its X closes it, and Retry
          reads the invoices again. */}
      {data && modal === "job-invoice" && params.get("invoice") !== "new" && params.get("invoice") !== null && invoiceRead && !invoice && (
        <ModalPending
          title={ENTITY_CONFIG.invoice.label}
          onClose={close}
          failure={`This ${invoiceWord} is no longer here.`}
          retrying={invoicesQuery.isFetching}
          retry={() => void invoicesQuery.refetch()}
        />
      )}
      {data && modal === "job-invoice" && invoice && (
        <JobInvoiceDrawer
          key={invoice.id}
          invoice={invoice}
          clientId={clientId}
          jobs={jobsOf(invoice.jobIds)}
          billable={billable}
          today={today}
          paying={params.get("confirm") === "paid"}
          stacked={stacked}
          onStack={() => stack(true)}
          onUnstack={() => stack(false)}
          onOpenJob={backToJob}
          onClose={close}
        />
      )}
      {/* A cost passed along opens here from a link that names it (TD-100, DD-045). */}
      {modal === "expense" && params.get("entry") && <ExpenseEntryModal key={params.get("entry")} entryId={params.get("entry") ?? ""} onClose={close} />}
      {modal === "expense-item" && params.get("tpl") && <ExpenseItemModal key={params.get("tpl")} templateId={params.get("tpl") ?? ""} confirm={params.get("confirm")} onClose={close} />}
    </PageLayout>
  );
}
